import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Check, CheckCircle2, Clock, Loader2, X } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { useToast } from "@/shared/hooks/use-toast";
import { cn } from "@/infrastructure/lib/utils";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";

import { JobsService } from "../services/jobsService";
import type { Job, JobRun } from "../types/job";
import { LIVE_RUN_STATUSES, agentReportOf } from "../utils/runs";
import { formatElapsed, testProgress, testStage, type TestStage } from "../utils/testProgress";

interface JobTestDialogProps {
  job: Job | null;
  productId: string;
  onClose: () => void;
  onPublish: (job: Job) => void;
  canPublish: boolean;
}

const POLL_MS = 5_000;
const STEPS: Array<"queued" | "working" | "result"> = ["queued", "working", "result"];

/**
 * O teste de um Job pela tela (AB#86605, AC6): roda o rascunho uma vez e
 * acompanha a execução até terminar. O teste é efêmero — não entra no
 * histórico e some em 24h —, então o resultado só existe aqui e no chat.
 *
 * Mostra as etapas que existem de fato (na fila, o agente trabalhando, o fim),
 * uma barra que anda sem prometer prazo e o cronômetro. Fechar a janela não
 * para nada: o agente segue trabalhando no servidor.
 */
export function JobTestDialog({ job, productId, onClose, onPublish, canPublish }: JobTestDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [executionId, setExecutionId] = useState<string | null>(null);
  const openedAt = useRef(Date.now());
  const [now, setNow] = useState(Date.now());
  const [expanded, setExpanded] = useState(false);

  // O teste roda o agente de verdade (efeito em sistemas externos): dispara uma vez
  // por job aberto, mesmo com o efeito executado duas vezes no StrictMode de dev.
  const startedFor = useRef<string | null>(null);

  useEffect(() => {
    setExecutionId(null);
    setExpanded(false);
    openedAt.current = Date.now();
    if (!job) {
      startedFor.current = null;
      return;
    }
    if (startedFor.current === job.id) return;
    const jobId = job.id;
    startedFor.current = jobId;
    JobsService.test(productId, jobId)
      .then((result) => startedFor.current === jobId && setExecutionId(result.execution_id))
      .catch((error) => {
        if (startedFor.current !== jobId) return;
        toast({ title: resolveApiErrorMessage(error, t), variant: "destructive" });
        onClose();
      });
    // `onClose` muda a cada render do pai; o teste dispara uma vez por job aberto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, productId]);

  const runQuery = useQuery<JobRun>({
    queryKey: ["job-run", productId, executionId],
    queryFn: () => JobsService.run(productId, executionId!),
    enabled: Boolean(executionId),
    // A execução nasce na fila: até o engine gravá-la, a leitura dá 404.
    retry: (count) => count < 3,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return !status || LIVE_RUN_STATUSES.has(status) ? POLL_MS : false;
    },
  });

  const run = runQuery.data;
  const stage = testStage(run?.status);
  const live = stage === "queued" || stage === "working";

  // O cronômetro só anda enquanto o teste roda.
  useEffect(() => {
    if (!job || !live) return;
    const tick = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(tick);
  }, [job, live]);

  const startedAt = run?.started_at ? new Date(run.started_at).getTime() : null;
  const endedAt = run?.completed_at ? new Date(run.completed_at).getTime() : null;
  const elapsed = (endedAt ?? now) - (startedAt ?? openedAt.current);
  const progress = testProgress(stage, startedAt ? now - startedAt : 0);

  const { report, pending, error } = agentReportOf(run);
  const actions = Array.isArray(report.actions) ? report.actions.length : 0;
  const writes = typeof report.writes === "number" ? report.writes : 0;

  return (
    <Dialog open={Boolean(job)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("jobs.test.title", { name: job?.name ?? "" })}</DialogTitle>
          <DialogDescription>{t("jobs.test.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-3">
          <Stepper stage={stage} />

          <div className="space-y-2">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-1000 ease-out",
                  stage === "failed" ? "bg-destructive" : stage === "completed" ? "bg-green-600" : "bg-primary",
                )}
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" /> {formatElapsed(elapsed)}
            </span>
          </div>

          {stage === "failed" && error && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive line-clamp-4">
              {error}
            </p>
          )}

          {!live && run && (
            <div className="space-y-3 rounded-md border border-border/60 p-3 text-sm">
              {report.summary ? (
                <div>
                  <p className={cn("whitespace-pre-wrap text-muted-foreground", !expanded && "line-clamp-4")}>
                    {report.summary}
                  </p>
                  {report.summary.length > 280 && (
                    <button
                      type="button"
                      className="mt-1 text-xs font-medium text-primary hover:underline"
                      onClick={() => setExpanded((value) => !value)}
                    >
                      {expanded ? t("jobs.test.showLess") : t("jobs.test.showMore")}
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground">{t("jobs.test.noReport")}</p>
              )}

              <div className="flex flex-wrap gap-2 text-xs">
                <Chip>{t("jobs.test.actions", { count: actions })}</Chip>
                <Chip>{t("jobs.test.writes", { count: writes })}</Chip>
                {pending.length > 0 && <Chip tone="warning">{t("jobs.test.pendingCount", { count: pending.length })}</Chip>}
              </div>

              {pending.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer font-medium">{t("jobs.test.pending")}</summary>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
                    {pending.map((item, index) => (
                      <li key={`${item}-${index}`}>{item}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("jobs.test.close")}
          </Button>
          {canPublish && job && run?.status === "completed" && !job.published && (
            <Button onClick={() => onPublish(job)}>{t("jobs.test.publish")}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Na fila → o agente trabalhando → o resultado, cada etapa com o seu estado. */
function Stepper({ stage }: { stage: TestStage }) {
  const { t } = useTranslation();
  const current = stage === "queued" ? 0 : stage === "working" ? 1 : 2;

  return (
    <ol className="flex items-center gap-4">
      {STEPS.map((step, index) => {
        const done = index < current || (index === 2 && stage === "completed");
        const active = index === current && (stage === "queued" || stage === "working");
        const failed = index === 2 && stage === "failed";
        const label =
          step === "result"
            ? stage === "completed"
              ? t("jobs.test.stages.completed")
              : stage === "failed"
                ? t("jobs.test.stages.failed")
                : t("jobs.test.stages.result")
            : t(`jobs.test.stages.${step}`);
        return (
          <li key={step} className="flex flex-1 items-center gap-4 last:flex-none">
            <span className="flex items-center gap-2 whitespace-nowrap text-sm font-medium">
              <span
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border",
                  done && "border-green-600 bg-green-600 text-white",
                  active && "border-primary text-primary",
                  failed && "border-destructive bg-destructive text-white",
                  !done && !active && !failed && "border-border text-muted-foreground",
                )}
              >
                {failed ? (
                  <X className="h-3.5 w-3.5" />
                ) : done ? (
                  index === 2 ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />
                ) : active ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <span className="text-xs">{index + 1}</span>
                )}
              </span>
              <span className={cn(!done && !active && !failed && "text-muted-foreground", failed && "text-destructive")}>
                {label}
              </span>
            </span>
            {index < STEPS.length - 1 && (
              <span className={cn("h-px flex-1", index < current ? "bg-green-600" : "bg-border")} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function Chip({ children, tone }: { children: ReactNode; tone?: "warning" }) {
  return (
    <span
      className={cn(
        "rounded-full border px-2 py-0.5",
        tone === "warning" ? "border-amber-500/50 text-amber-600 dark:text-amber-400" : "border-border text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}
